import React, { useEffect, useState, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import { getAllNcrs, deleteNcr, type Ncr } from '../../services/ncrService.ts';
import './NcrList.css';

const NcrList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [ncrs, setNcrs] = useState<Ncr[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchNcrs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllNcrs();
      setNcrs(data);
    } catch (error: any) {
      notify(error.message || 'Failed to fetch NCRs', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchNcrs();
  }, [fetchNcrs]);

  const handleDelete = async (id: number | undefined) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this NCR?')) return;

    try {
      await deleteNcr(id);
      notify('NCR deleted successfully', { type: 'info' });
      setNcrs(ncrs.filter((n) => n.id !== id));
    } catch (error: any) {
      notify(error.message || 'Failed to delete NCR', { type: 'warning' });
    }
  };

  const filteredNcrs = ncrs.filter((ncr) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      ncr.ncrNumber.toLowerCase().includes(searchLower) ||
      (ncr.description && ncr.description.toLowerCase().includes(searchLower)) ||
      (ncr.templateName && ncr.templateName.toLowerCase().includes(searchLower))
    );
  });

  const getStatusBadgeClass = (status?: string) => {
    const baseClass = 'status-badge';
    switch ((status || '').toLowerCase()) {
      case 'open':
        return `${baseClass} open`;
      case 'in-progress':
        return `${baseClass} in-progress`;
      case 'close':
        return `${baseClass} close`;
      default:
        return `${baseClass} open`;
    }
  };

  const getStatusLabel = (status?: string) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'open') {
      return 'Open';
    }
    if (normalized === 'in-progress') {
      return 'In Progress';
    }
    if (normalized === 'close') {
      return 'Close';
    }
    return 'Open';
  };

  return (
    <div className="ncr-list-container">
      <div className="ncr-list-header">
        <div className="header-title">
          <h1>NCR Management</h1>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/Ncr/create')}
        >
          <Plus size={18} />
          Create NCR
        </button>
      </div>

      <div className="ncr-list-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by NCR Number, Description, or Template..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="ncr-list-table-wrapper">
        {loading ? (
          <div className="loading-state">Loading NCRs...</div>
        ) : filteredNcrs.length === 0 ? (
          <div className="empty-state">
            <p>No NCRs found</p>
            <button
              className="btn-secondary"
              onClick={() => navigate('/Ncr/create')}
            >
              Create Your First NCR
            </button>
          </div>
        ) : (
          <table className="ncr-list-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Template</th>
                <th>Description</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredNcrs.map((ncr) => (
                <tr key={ncr.id}>
                  <td className="ncr-number">{ncr.ncrNumber || '-'}</td>
                  <td>{ncr.templateName || '-'}</td>
                  <td className="description" title={ncr.description}>
                    {ncr.description || '-'}
                  </td>
                  <td>{ncr.ownerName || '-'}</td>
                  <td>
                    <span className={getStatusBadgeClass(ncr.status)}>
                      {getStatusLabel(ncr.status)}
                    </span>
                  </td>
                  <td>
                    {ncr.createdAt ? new Date(ncr.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-view"
                      title="View Details"
                      onClick={() => navigate(`/Ncr/${ncr.id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="btn-icon btn-edit"
                      title="Edit"
                      onClick={() => navigate(`/Ncr/${ncr.id}/edit`)}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      title="Delete"
                      onClick={() => handleDelete(ncr.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ncr-list-footer">
        <p>Total: {filteredNcrs.length} NCRs</p>
      </div>
    </div>
  );
};

export default NcrList;
